<template>
  <div class="lws-warehouse-container">
    <!-- 统一导航栏 -->
    <headers />

    <!-- 主体内容区域 -->
    <div class="container mt-30 mb-30">
      <el-card class="box-card">
        <!-- 页面头部标题及搜索区 -->
        <div class="page-header">
          <div class="page-title">
            <icon-svg iconClass="#icon-danwei" class="title-icon-svg"></icon-svg
            ><span class="theme-text">LWS良仓</span> · 授权经销商
          </div>
          <div class="filter-right">
            <el-select
              v-model="selectedRegion"
              placeholder="选择大区"
              clearable
              class="custom-select"
              style="margin-right: 12px; width: 160px"
              @change="handleSearch"
            >
              <el-option
                v-for="item in regionOptions"
                :key="item"
                :label="item"
                :value="item"
              />
            </el-select>
            <el-input
              v-model="searchQuery"
              placeholder="输入经销商名称搜索..."
              clearable
              prefix-icon="el-icon-search"
              class="custom-search-input"
              style="width: 240px"
              @input="handleSearch"
            />
          </div>
        </div>

        <!-- 经销商表格列表 -->
        <div class="table-container">
          <el-table
            v-loading="loading"
            element-loading-text="正在努力加载最新授权名录..."
            :data="filteredData"
            border
            stripe
            class="custom-table"
            :header-cell-style="headerCellStyle"
          >
            <el-table-column
              prop="region"
              label="大区"
              width="200"
              align="center"
              class-name="region-col"
            >
              <template slot-scope="scope">
                <span class="region-name">{{ scope.row.region }}</span>
              </template>
            </el-table-column>
            <el-table-column
              prop="dealer"
              label="授权经销商"
              header-align="center"
              align="center"
            >
              <template slot-scope="scope">
                <span class="dealer-name">{{ scope.row.dealer }}</span>
              </template>
            </el-table-column>
          </el-table>

          <!-- 空数据状态 -->
          <div v-if="filteredData.length === 0 && !loading" class="empty-state">
            <i class="el-icon-document-delete empty-icon"></i>
            <p class="empty-text">暂无符合条件的经销商数据</p>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 统一页脚 -->
    <footers />
  </div>
</template>

<script>
import headers from "@/components/header.vue";
import footers from "@/components/footer.vue";
import request from "@/utils/request";

export default {
  name: "LwsWarehouseIndex",
  components: {
    headers,
    footers,
  },
  data() {
    return {
      // 搜索关键字
      searchQuery: "",
      // 选择的大区
      selectedRegion: "",
      // 大区下拉框固定选项
      regionOptions: [
        "上海大区",
        "华东大区",
        "华北大区",
        "华南大区",
        "华西大区",
        "华中大区",
      ],
      // 原始经销商数据
      allDealers: [],
      // 过滤后的经销商数据
      filteredData: [],
      // 合并单元格 of table mapping items
      spanArr: [],
      // 合并单元格的位置指针
      pos: 0,
      // 表格加载状态
      loading: false,
      // 搜索防抖定时器
      searchTimer: null,
    };
  },
  created() {
    this.initData();
  },
  methods: {
    /**
     * 异步拉取接口，获取良仓授权经销商列表数据。
     * @name initData
     * @returns {Promise<void>}
     */
    async initData() {
      this.loading = true;
      try {
        const response = await request({
          url: "/pc/lws/warehouse/listAll",
          method: "get",
          params: {
            name: this.searchQuery.trim(),
            region: this.selectedRegion || "",
          },
        });

        // 统一的接口返回判定
        if (response.success || response.code === 200) {
          const list = response.data || [];

          // 安全映射字段名，保持对老表格组件的完美向下兼容
          const mapped = list.map((item) => ({
            region: item.region || "",
            dealer: item.name || item.dealer || "",
          }));

          this.allDealers = this.sortDealers(mapped);
          this.filteredData = [...this.allDealers];
        } else {
          throw new Error(response.msg || "接口响应失败");
        }
      } catch (error) {
        console.error("加载良仓授权名录失败:", error);
        this.$message.error("获取授权经销商数据失败，请稍后重试！");
      } finally {
        this.computeSpanArr();
        this.loading = false;
      }
    },

    /**
     * 实时检索大区名称或授权经销商名称（防抖后端过滤）
     * @name handleSearch
     * @returns {void}
     */
    handleSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.initData();
      }, 300);
    },

    /**
     * 将经销商列表按统一的大区顺序进行排序
     * @name sortDealers
     * @param {Array} list - 待排序列表
     * @returns {Array} 排序后的列表
     */
    sortDealers(list) {
      const regionOrder = [
        "上海大区",
        "华东大区",
        "华北大区",
        "华南大区",
        "华西大区",
        "华中大区",
      ];
      return [...list].sort((a, b) => {
        const indexA = regionOrder.indexOf(a.region);
        const indexB = regionOrder.indexOf(b.region);
        const orderA = indexA === -1 ? 99 : indexA;
        const orderB = indexB === -1 ? 99 : indexB;
        return orderA - orderB;
      });
    },

    /**
     * 根据当前过滤或展示的数据集，动态计算合并单元格记录数组 spanArr
     * @name computeSpanArr
     * @returns {void}
     */
    computeSpanArr() {
      this.spanArr = [];
      this.pos = 0;
      const data = this.filteredData;
      for (let i = 0; i < data.length; i++) {
        if (i === 0) {
          this.spanArr.push(1);
          this.pos = 0;
        } else {
          // 若当前行的大区与上一行一致，说明属于同一个合并的大区，将上一行所占行数加一，当前行占0行
          if (data[i].region === data[i - 1].region) {
            this.spanArr[this.pos] += 1;
            this.spanArr.push(0);
          } else {
            // 否则作为新大区合并的起始行，占1行
            this.spanArr.push(1);
            this.pos = i;
          }
        }
      }
    },

    /**
     * Element UI 表格合并单元格逻辑函数
     * @name cellMerge
     * @param {Object} param - 行列及合并控制参数
     * @param {Object} param.row - 行记录数据
     * @param {Object} param.column - 列属性配置
     * @param {number} param.rowIndex - 行索引
     * @param {number} param.columnIndex - 列索引
     * @returns {Object|undefined} 返回合并参数对象 { rowspan, colspan }
     */
    cellMerge({ row, column, rowIndex, columnIndex }) {
      // 仅对第一列“大区”（columnIndex === 0）进行合并单元格处理
      if (columnIndex === 0) {
        const _row = this.spanArr[rowIndex];
        const _col = _row > 0 ? 1 : 0;
        return {
          rowspan: _row,
          colspan: _col,
        };
      }
    },

    /**
     * 自定义表格表头样式，注入优雅的轻量主题色
     * @name headerCellStyle
     * @returns {Object} 样式声明对象
     */
    headerCellStyle() {
      return {
        fontWeight: "700",
        fontSize: "15px",
        height: "52px",
        textAlign: "center",
        backgroundColor: "#f7f9fa",
      };
    },
  },
};
</script>

<style lang="less" scoped>
.lws-warehouse-container {
  width: 100%;
  min-height: 100vh;
  // background-color: #f7f9fa;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  .box-card {
    border-radius: 8px;
    border: 1px solid #ebeef5;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05) !important;
    padding: 10px;
  }

  /* 页面简易标题及搜索区样式 */
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 16px;

    .page-title {
      font-size: 20px;
      font-weight: bold;
      color: #303133;
      display: flex;
      align-items: center;
      gap: 10px;

      .title-icon-svg {
        width: 22px;
        height: 22px;
        fill: #00d8c3;
        vertical-align: middle;
      }

      .theme-text {
        color: #00d8c3;
      }
    }

    .filter-right {
      display: flex;
      align-items: center;
    }

    .custom-select {
      /deep/ .el-input__inner {
        border-radius: 4px;
        border-color: #dcdfe6;
        transition: border-color 0.2s;

        &:focus,
        &:hover {
          border-color: #00d8c3;
        }
      }
    }

    .custom-search-input {
      width: 240px;

      /deep/ .el-input__inner {
        border-radius: 4px;
        border-color: #dcdfe6;
        transition: border-color 0.2s;

        &:focus {
          border-color: #00d8c3;
        }
      }
    }
  }

  /* 表格容器样式 */
  .table-container {
    width: 100%;

    .custom-table {
      width: 100%;

      /* 大区名称样式，采用纯粹精美的加粗墨绿字 */
      .region-name {
        font-size: 14px;
        font-weight: bold;
        color: #007a6f;
      }

      /* 经销商名称样式 */
      .dealer-name {
        font-size: 14px;
        color: #4b5563;
        padding-left: 24px;
      }

      /* 自定义 Element 默认表头加粗 */
      /deep/ .el-table__header-wrapper th {
        font-weight: bold;
        font-size: 15px;
      }
    }

    /* 空状态样式 */
    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 60px 0;
      color: #909399;

      .empty-icon {
        font-size: 48px;
        margin-bottom: 12px;
        color: #dcdfe6;
      }

      .empty-text {
        font-size: 14px;
        margin: 0;
      }
    }
  }
}
</style>
